import identity from 'lodash/identity';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ISSUE_VIEW,
	BOARD_CARD_TRANSITION,
	ISSUE_VIEW_SHORTCUT_DIALOG_TRANSITION,
	ISSUE_RESULTS_SHORTCUT_TRANSITION,
	ADVANCED_ROADMAPS_JIRA_TRANSITION,
	COMMAND_PALETTE_STATUS_FIELD_TRANSITION,
	ISSUE_TRANSITION_STATUS_FIELD_TRIGGER,
} from '@atlassian/jira-issue-transition-common-types';
import {
	SELECT_CF_TYPE,
	TEXT_CF_TYPE,
	URL_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	ASSIGNEE_TYPE,
	RESOLUTION_TYPE,
	LABELS_TYPE,
	DATE_CF_TYPE,
	DATETIME_CF_TYPE,
	PRIORITY_TYPE,
	ISSUE_TYPE,
	FIX_VERSIONS_TYPE,
	NUMBER_CF_TYPE,
	USER_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	REPORTER_TYPE,
	CASCADING_SELECT_CF_TYPE,
	DESCRIPTION_TYPE,
	PARENT_TYPE,
	MULTI_USER_CF_TYPE,
} from '@atlassian/jira-platform-field-config';
import {
	cancelTTI,
	markFieldsRegistered,
	markIframeLoad,
	markOnInitResolved,
	stopTTI,
} from '@atlassian/jira-ui-modifications-analytics/src/common/utils/performance-analytics/issue-transition';
import {
	LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '../../common/constants';
import {
	assigneeGetValueTransformer,
	assigneeSetValueTransformer as assigneeSetValueTransformerCommon,
	assigneeSetValueValidator,
} from '../../common/fields/assignee';
import { validateCascadingSelectSetValue } from '../../common/fields/cascading-select';
import {
	datePickerGetValueTransformer,
	datePickerSetValueTransformer,
	datePickerSetValueValidator,
} from '../../common/fields/date-picker';
import {
	transformDateTimePickerGetValue,
	transformDateTimePickerSetValue,
	validateDateTimePickerSetValue,
} from '../../common/fields/date-time-picker';
import { issueTypeSetValueValidator } from '../../common/fields/issue-type';
import { labelsGetValueTransformer, labelsSetValueTransformer } from '../../common/fields/labels';
import { validateMultiCheckboxSetValue } from '../../common/fields/multi-checkbox';
import { validateMultiSelectSetValue } from '../../common/fields/multi-select';
import { validateMultiUserPickerSetValue } from '../../common/fields/multi-user-picker';
import { validateNumberValue } from '../../common/fields/number';
import { parentSetValueValidator } from '../../common/fields/parent';
import { validatePrioritySetValue } from '../../common/fields/priority';
import { radioButtonsSetValueValidator } from '../../common/fields/radio-buttons';
import {
	transformSingleLineTextToPublicValue,
	validateSingleLineTextValue,
} from '../../common/fields/single-line-text';
import { userPickerSetValueValidator } from '../../common/fields/user-picker';
import type {
	BaseFieldConfiguration,
	ViewConfiguration,
	AnalyticsResult,
} from '../../common/types';
import {
	getCommonFieldValidators,
	validateSetOptionsVisibilityRule,
} from '../../common/utils/get-common-field-validators';
import {
	transformSetOptionsVisibilityAri,
	validateSetOptionsVisibility,
} from '../../common/utils/options-visibility';
import type { FieldType } from '../types';
import { transformAssigneeSetValue } from './fields/assignee';
import {
	transformGetValueCascadingSelect,
	transformSetValueCascadingSelect,
} from './fields/cascading-select';
import {
	issueTypeGetValueTransformer,
	issueTypeSetValueTransformer,
	validateSetOptionsVisibilityIssueType,
	transformIssueTypeSetOptionsVisibility,
} from './fields/issue-type';
import { validateLabelsSetValue } from './fields/labels';
import {
	multiCheckboxGetValueTransformer,
	multiCheckboxSetValueTransformer,
	oldMultiCheckboxGetValueTransformer,
	oldMultiCheckboxSetValueTransformer,
	oldTransformMultiCheckboxSetOptionsVisibility,
	transformMultiCheckboxGetOptionsVisibility,
	transformMultiCheckboxSetOptionsVisibility,
} from './fields/multi-checkbox';
import {
	multiSelectGetValueTransformer,
	multiSelectSetValueTransformer,
} from './fields/multi-select';
import {
	transformMultiUserPickerGetValue,
	transformMultiUserPickerSetValue,
} from './fields/multi-user-picker';
import {
	paragraphGetValueTransformer,
	paragraphSetValueTransformer,
	paragraphSetValueValidator,
} from './fields/paragraph';
import {
	transformParentSetValue,
	transformParentSetOptionsVisibility,
	transformParentGetValue,
} from './fields/parent';
import {
	priorityGetValueTransformer,
	prioritySetValueTransformer,
	transformSetOptionsVisibilityAriPriority,
} from './fields/priority';
import {
	radioButtonsGetValueTransformerOld,
	radioButtonsSetValueTransformerOld,
	transformRadioButtonsGetOptionsVisibility,
	transformRadioButtonsGetValue,
	transformRadioButtonsSetOptionsVisibility,
	transformRadioButtonsSetOptionsVisibilityOld,
	transformRadioButtonsSetValue,
} from './fields/radio-buttons';
import {
	transformReleaseVersionsGetValue,
	releaseVersionsRequiredScopes,
	transformReleaseVersionsSetValue,
	validateReleaseVersionValue,
	transformReleaseVersionsSetOptionsVisibility,
} from './fields/release-versions';
import {
	resolutionGetValueTransformer,
	resolutionSetValueTransformer,
	resolutionSetValueValidator,
	transformSetOptionsVisibilityAriResolution,
} from './fields/resolution';
import {
	selectSetValueValidator,
	selectSetValueTransformer,
	selectGetValueTransformer,
	transformSelectGetOptionsVisibility,
	transformSelectSetOptionsVisibility,
	selectSetValueTransformerOld,
	selectGetValueTransformerOld,
} from './fields/select';
import { transformUserPickerSetValue, transformUserPickerGetValue } from './fields/user-picker';
import messages from './messages';

export class IssueTransitionViewConfiguration implements ViewConfiguration {
	private analytics: AnalyticsResult = {
		stopTTI,
		cancelTTI,
		markIframeLoad,
		markFieldsRegistered,
		markOnInitResolved,
	};

	getSupportedFieldsConfiguration(): Record<FieldType, BaseFieldConfiguration> {
		const commonFieldValidators = getCommonFieldValidators();

		return {
			[TEXT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: transformSingleLineTextToPublicValue,
				},
				emitChangeOn: 'blur',
			},
			[SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: selectSetValueValidator,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? validateSetOptionsVisibility
						: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? selectSetValueTransformer
						: selectSetValueTransformerOld,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? transformSetOptionsVisibilityAri
						: transformSelectSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? selectGetValueTransformer
						: selectGetValueTransformerOld,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? identity
						: transformSelectGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				...(ff('modern-issue-transition-fields-rollout-batch-2_bremb')
					? {}
					: { lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY }),
				propertyMapping: {
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? 'fieldOptionsFilter'
						: 'allowedValues',
				},
			},
			[URL_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: transformSingleLineTextToPublicValue,
				},
				emitChangeOn: 'blur',
			},
			[MULTI_CHECKBOXES_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateMultiCheckboxSetValue,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? multiCheckboxSetValueTransformer
						: oldMultiCheckboxSetValueTransformer,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? transformMultiCheckboxSetOptionsVisibility
						: oldTransformMultiCheckboxSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? multiCheckboxGetValueTransformer
						: oldMultiCheckboxGetValueTransformer,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? identity
						: transformMultiCheckboxGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[RADIO_BUTTONS_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: radioButtonsSetValueValidator,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? transformRadioButtonsSetValue
						: radioButtonsSetValueTransformerOld,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? transformRadioButtonsSetOptionsVisibility
						: transformRadioButtonsSetOptionsVisibilityOld,
				},
				internalToPublicTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? transformRadioButtonsGetValue
						: radioButtonsGetValueTransformerOld,
					optionsVisibility: ff('modern-issue-transition-fields-rollout-batch-2_bremb')
						? identity
						: transformRadioButtonsGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[TEXT_AREA_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: paragraphSetValueValidator,
				},
				publicToInternalTransformers: {
					value: paragraphSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: paragraphGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[DESCRIPTION_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: paragraphSetValueValidator,
				},
				publicToInternalTransformers: {
					value: paragraphSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: paragraphGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[MULTI_SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateMultiSelectSetValue,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: multiSelectSetValueTransformer,
					optionsVisibility: transformSetOptionsVisibilityAri,
				},
				internalToPublicTransformers: {
					value: multiSelectGetValueTransformer,
					/**
					 * Note: `transformFieldToIframe()` uses `FieldMapFromIframe` as input of optionsVisibility transformer,
					 * we're using `identity()` to return that same value and override `formData` (containing the private shape), before we sent it back to the iframe.
					 *
					 * Check "src/packages/issue-adjustments/issue-adjustments/src/controllers/issue-adjustments/main.tsx" for details.
					 */
					optionsVisibility: identity,
				},
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
				emitChangeOn: 'change',
			},
			[ASSIGNEE_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: assigneeSetValueValidator,
				},
				publicToInternalTransformers: {
					value: ff('modern-issue-transition-fields-rollout-batch-5_gg7wo')
						? transformAssigneeSetValue
						: assigneeSetValueTransformerCommon,
				},
				internalToPublicTransformers: {
					value: assigneeGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[USER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: userPickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: transformUserPickerSetValue,
				},
				internalToPublicTransformers: {
					value: transformUserPickerGetValue,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[REPORTER_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: userPickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: transformUserPickerSetValue,
				},
				internalToPublicTransformers: {
					value: transformUserPickerGetValue,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[RESOLUTION_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: resolutionSetValueValidator,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: resolutionSetValueTransformer,
					optionsVisibility: transformSetOptionsVisibilityAriResolution,
				},
				internalToPublicTransformers: {
					value: resolutionGetValueTransformer,
					optionsVisibility: identity,
				},
				emitChangeOn: 'change',
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
			},
			[LABELS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateLabelsSetValue,
				},
				publicToInternalTransformers: {
					value: labelsSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: labelsGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[DATE_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: datePickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: datePickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: datePickerGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[DATETIME_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateDateTimePickerSetValue,
				},
				publicToInternalTransformers: {
					value: transformDateTimePickerSetValue,
				},
				internalToPublicTransformers: {
					value: transformDateTimePickerGetValue,
				},
				emitChangeOn: 'change',
			},
			[PRIORITY_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validatePrioritySetValue,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: prioritySetValueTransformer,
					optionsVisibility: transformSetOptionsVisibilityAriPriority,
				},
				internalToPublicTransformers: {
					value: priorityGetValueTransformer,
					optionsVisibility: identity,
				},
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
				emitChangeOn: 'change',
			},
			[ISSUE_TYPE]: {
				publicShapeValidators: {
					value: issueTypeSetValueValidator,
					optionsVisibility: validateSetOptionsVisibilityIssueType,
				},
				publicToInternalTransformers: {
					value: issueTypeSetValueTransformer,
					optionsVisibility: transformIssueTypeSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: issueTypeGetValueTransformer,
					optionsVisibility: identity,
				},
				emitChangeOn: 'change',
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
			},
			[NUMBER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateNumberValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {},
				emitChangeOn: 'blur',
			},
			[PARENT_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: parentSetValueValidator,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: transformParentSetValue,
					optionsVisibility: transformParentSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: transformParentGetValue,
					optionsVisibility: identity,
				},
				emitChangeOn: 'change',
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
			},
			[FIX_VERSIONS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateReleaseVersionValue,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: transformReleaseVersionsSetValue,
					optionsVisibility: transformReleaseVersionsSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: transformReleaseVersionsGetValue,
					optionsVisibility: identity,
				},
				emitChangeOn: 'change',
				requiredScopes: releaseVersionsRequiredScopes,
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
			},
			[AFFECTS_VERSIONS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateReleaseVersionValue,
					optionsVisibility: validateSetOptionsVisibility,
				},
				publicToInternalTransformers: {
					value: transformReleaseVersionsSetValue,
					optionsVisibility: transformReleaseVersionsSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: transformReleaseVersionsGetValue,
					optionsVisibility: identity,
				},
				emitChangeOn: 'change',
				requiredScopes: releaseVersionsRequiredScopes,
				propertyMapping: {
					optionsVisibility: 'fieldOptionsFilter',
				},
			},
			[CASCADING_SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateCascadingSelectSetValue,
				},
				publicToInternalTransformers: {
					value: transformSetValueCascadingSelect,
				},
				internalToPublicTransformers: {
					value: transformGetValueCascadingSelect,
				},
				emitChangeOn: 'change',
			},
			[MULTI_USER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateMultiUserPickerSetValue,
				},
				publicToInternalTransformers: {
					value: transformMultiUserPickerSetValue,
				},
				internalToPublicTransformers: {
					value: transformMultiUserPickerGetValue,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
		};
	}

	getSupportedFieldTypes() {
		const supportedFieldTypes: FieldType[] = [
			TEXT_CF_TYPE,
			SELECT_CF_TYPE,
			URL_CF_TYPE,
			MULTI_CHECKBOXES_CF_TYPE,
			RADIO_BUTTONS_CF_TYPE,
			TEXT_AREA_CF_TYPE,
			MULTI_SELECT_CF_TYPE,
			ASSIGNEE_TYPE,
			RESOLUTION_TYPE,
			DATE_CF_TYPE,
			DATETIME_CF_TYPE,
			LABELS_TYPE,
			PRIORITY_TYPE,
			ISSUE_TYPE,
			NUMBER_CF_TYPE,
			USER_CF_TYPE,
			FIX_VERSIONS_TYPE,
			AFFECTS_VERSIONS_TYPE,
			REPORTER_TYPE,
			CASCADING_SELECT_CF_TYPE,
			PARENT_TYPE,
			DESCRIPTION_TYPE,
			MULTI_USER_CF_TYPE,
		];

		return supportedFieldTypes;
	}

	getSupportedProjectTypes() {
		return [SOFTWARE_PROJECT];
	}

	getConsentMessage() {
		return messages.uimTransitionViewConsentMessage;
	}

	getAnalytics() {
		return this.analytics;
	}

	getSupportedTriggerPointsConfiguration() {
		if (ff('ui-modifications-on-issue-transition-view_7z1g0')) {
			return new Set([
				ISSUE_VIEW,
				BOARD_CARD_TRANSITION,
				ADVANCED_ROADMAPS_JIRA_TRANSITION,
				ISSUE_VIEW_SHORTCUT_DIALOG_TRANSITION,
				COMMAND_PALETTE_STATUS_FIELD_TRANSITION,
				ISSUE_RESULTS_SHORTCUT_TRANSITION,
				ISSUE_TRANSITION_STATUS_FIELD_TRIGGER,
			] as const);
		}
		return new Set([]);
	}

	isScreenTabsSupportEnabled = () => false;
}
